import { useAsyncAction } from "nate-react-api-helpers";
import { useEffect, useRef, useState } from "react";
import { useSyncedRef } from "./SyncedRef";

// let typing settle before an auto-save goes out
const textDebounceMs = 800;

// useAutoSave writes an editor's contents as they're changed, so nothing needs a save
// button: the text fields wait for a pause in typing, everything else goes out right
// away, and an edit still waiting when the editor closes is sent on the way out.
export function useAutoSave<TInput>(props: {
    // input is the whole of what a save posts
    input: TInput;

    save: (input: TInput) => Promise<void>;

    // off holds every save back, for an editor with nothing to write to yet
    off?: boolean;

    // text is the fields that wait for a pause in typing, fields the rest
    text: any[];
    fields: any[];
}) {
    const inputRef = useSyncedRef(props.input);
    const saveRef = useSyncedRef(props.save);
    const [saved, setSaved] = useState(false);

    // an auto-save carrying no change is just noise, so compare against what the
    // server was last given
    const sent = useRef(JSON.stringify(props.input));

    // one save at a time: two in flight can reach the server out of order, and the
    // older one would win. A change made during a save goes out once it's done.
    const running = useRef(false);

    const action = useAsyncAction(async () => {
        running.current = true;

        try {
            while (JSON.stringify(inputRef.current) !== sent.current) {
                const posted = inputRef.current;
                await saveRef.current(posted);
                sent.current = JSON.stringify(posted);
                setSaved(true);
            }
        } finally {
            running.current = false;
        }
    }, []);

    const actionRef = useSyncedRef(action.callback);
    const saveChanged = useRef(() => {});
    saveChanged.current = () => {
        if (running.current) return;
        if (JSON.stringify(inputRef.current) === sent.current) return;
        actionRef.current(null);
    };

    const off = !!props.off;

    const pendingText = useRef(false);
    const startedText = useRef(false);
    useEffect(() => {
        if (off) return;
        if (!startedText.current) {
            startedText.current = true;
            return;
        }

        pendingText.current = true;
        const timer = setTimeout(() => {
            pendingText.current = false;
            saveChanged.current();
        }, textDebounceMs);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [off, ...props.text]);

    const startedFields = useRef(false);
    useEffect(() => {
        if (off) return;
        if (!startedFields.current) {
            startedFields.current = true;
            return;
        }

        saveChanged.current();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [off, ...props.fields]);

    // don't drop a text edit that was still waiting when the editor closed
    useEffect(() => () => {
        if (pendingText.current) saveChanged.current();
    }, []);

    return {
        loading: action.loading,
        error: action.error,

        // saved says a change has in fact reached the server since the editor opened
        saved: saved,
    };
}
